import { useEffect, useState } from "react";
import { Modal, Button, Typography, Checkbox, Empty } from "antd";
import { ReactComponent as DepartmentIcon } from "../../assets/images/reusable-items/department.svg";
import { CheckOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  selectDepartments,
  selectDepartmentsLoaded,
} from "../../redux/educator/selector";
import { departmentTypes as types } from "../../redux/educator/actions";
import showNotification from "../../services/notificationService";
import { selectUser } from "../../redux/auth/selector";

const { Text } = Typography;

const DepartmentSelector = ({
  departments,
  onSelectionChange,
  selectedDepartments,
  setSelectedDepartments,
  isDetailsState,
  isAssignmentDetailsPage,
}: any) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tempSelectedDepartments, setTempSelectedDepartments] = useState<
    string[]
  >([]);
  const educatorDepartments = useSelector(selectDepartments);
  const departmentsLoaded = useSelector(selectDepartmentsLoaded);
  const user = useSelector(selectUser);
  const [concatedDepartments, setConcatedDepartments] = useState<any>([]);
  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    departments &&
      educatorDepartments &&
      setConcatedDepartments(
        departments.concat(
          educatorDepartments.filter(
            (item2) => !departments.some((item1: any) => item1.id === item2.id)
          )
        )
      );
  }, [departments, educatorDepartments]);

  useEffect(() => {
    if (!departmentsLoaded) {
      dispatch({ type: types.GET_DEPARTMENT });
    }
  }, []);

  const handleOk = () => {
    setIsModalVisible(false);
    setSelectedDepartments(tempSelectedDepartments);
    if (onSelectionChange) {
      onSelectionChange(tempSelectedDepartments);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setTempSelectedDepartments(selectedDepartments);
  };

  const handleSelect = (dept: any) => {
    const educatorDepartmentIds = educatorDepartments.map((dep) => dep.id);

    const hasAccess = educatorDepartmentIds.some(
      (educatorDepartmentId) => educatorDepartmentId === dept
    );
    if (!hasAccess) {
      showNotification(
        "error",
        "Sorry, you do not have access to this department."
      );
      return;
    }

    setTempSelectedDepartments((prevSelected: string[]) =>
      prevSelected.includes(dept)
        ? prevSelected.filter((item) => item !== dept)
        : [...prevSelected, dept]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    const allDepartmentIds = concatedDepartments.map((dept: any) => dept.id);
    if (checked) {
      setTempSelectedDepartments(allDepartmentIds);
    } else {
      if (!user.hasAccessToAllDepartments) {
        const educatorDepartmentIds = educatorDepartments.map((dep) => dep.id);
        const selected = allDepartmentIds.filter(
          (depIds: any) => !educatorDepartmentIds.includes(depIds)
        );
        setTempSelectedDepartments(selected);
      } else {
        setTempSelectedDepartments([]);
      }
    }
  };

  const openModal = () => {
    setTempSelectedDepartments(selectedDepartments);
    setIsModalVisible(true);
  };

  const handleExpand = () => {
    if (!isDetailsState) return;
    setExpanded(!expanded);
  };

  return (
    <>
      <div
        onClick={handleExpand}
        className={`w-full px-6 py-4 rounded-lg shadow-md  ${
          !isDetailsState ? " bg-white" : "bg-neutral-100"
        } 
        ${isDetailsState && selectedDepartments?.length > 0 && "cursor-pointer"}
        `}
      >
        <div className="flex justify-between items-center">
          <div className="flex gap-3 items-center">
            <DepartmentIcon className="fill-primary-400 w-[20px] h-[20px]" />
            <Text
              className={`text-neutral-600 text-sm ${
                selectedDepartments?.length > 0 ? "" : "font-semibold"
              } 
              ${isDetailsState && "font-semibold"}
              `}
            >
              {isDetailsState
                ? `Departments ${
                    selectedDepartments?.length > 0
                      ? `(${selectedDepartments?.length})`
                      : ""
                  }`
                : selectedDepartments?.length > 0
                ? `Selected Departments (${selectedDepartments?.length})`
                : "Departments(s)"}
            </Text>
          </div>
          {!isDetailsState && (
            <Button
              onClick={openModal}
              className="border-none text-primary-600 text-base font-semibold shadow-none"
            >
              {selectedDepartments?.length === 0 ? "Select" : "Add More"}
            </Button>
          )}
        </div>
        {selectedDepartments?.length > 0 && (
          <div
            style={{
              height: isDetailsState
                ? !expanded
                  ? "0px"
                  : `calc(${selectedDepartments.length * 49 + 12}px)`
                : "auto",
              maxHeight: !isDetailsState ? "184px" : "auto",
            }}
            className={`flex flex-col gap-1 transition-all duration-500 ${
              isDetailsState && !expanded
                ? "overflow-hidden opacity-0"
                : `overflow-auto opacity-100 border-t border-t-primary-200 pt-4 mt-2`
            }`}
          >
            {selectedDepartments?.map((deptId: string) => {
              const dept = concatedDepartments?.find(
                (d: any) => d.id === deptId
              );

              return (
                <div
                  key={deptId}
                  className="flex items-center px-4 py-3 rounded-lg bg-neutral-100"
                >
                  <Text className="text-neutral-600 text-sm">{dept?.name}</Text>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        closeIcon={null}
      >
        {concatedDepartments.length > 0 ? (
          <>
            <div className="flex justify-between items-center pb-4 mb-4 border-b border-b-neutral-200">
              <div className="flex gap-3 items-center">
                <DepartmentIcon className="fill-primary-400 w-[20px] h-[20px]" />
                <Text className="text-neutral-400 font-normal text-sm">
                  Department(s)
                </Text>
              </div>
              <Checkbox
                onChange={(e) => handleSelectAll(e.target.checked)}
                checked={
                  tempSelectedDepartments?.length ===
                  concatedDepartments?.length
                }
              >
                <Text className="text-neutral-400 font-normal text-sm">
                  Select All
                </Text>
              </Checkbox>
            </div>
            <div className="flex flex-col max-h-[calc(100vh-300px)] overflow-y-auto">
              {departments &&
                educatorDepartments &&
                concatedDepartments?.map((dept: any, index: any) => (
                  <div
                    key={index}
                    onClick={() => handleSelect(dept.id)}
                    className={`flex items-center px-4 py-3 mb-2 rounded-lg cursor-pointer ${
                      tempSelectedDepartments?.includes(dept.id)
                        ? "bg-primary-100"
                        : "bg-neutral-100"
                    } hover:bg-primary-200 transition`}
                  >
                    <Text
                      className={`flex-1 text-neutral-600 text-sm ${
                        tempSelectedDepartments?.includes(dept.id)
                          ? "font-semibold"
                          : ""
                      }`}
                    >
                      {dept.name}
                    </Text>
                    {tempSelectedDepartments?.includes(dept.id) && (
                      <CheckOutlined className="text-blue-500" />
                    )}
                  </div>
                ))}
            </div>
          </>
        ) : (
          <Empty className="py-5" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Modal>
    </>
  );
};

export default DepartmentSelector;
